package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.AuditEntry
import br.com.vvrefeicoes.portal.domain.model.AuditResult
import br.com.vvrefeicoes.portal.domain.repository.AuditPage
import br.com.vvrefeicoes.portal.domain.repository.AuditRepository
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonPrimitive

class RemoteAuditRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : AuditRepository {
    override suspend fun loadEntries(
        page: Int,
        size: Int,
        from: String?,
        to: String?,
        query: String?,
    ): AuditPage {
        val query =
            buildList {
                add("page=$page")
                add("size=$size")
                from?.let { add("from=$it") }
                to?.let { add("to=$it") }
                query?.takeIf { it.isNotBlank() }?.let { add("query=${it.encodeURLParameter()}") }
            }.joinToString("&")
        val response = get<AuditPageDto<AuditEventDto>>("/audit-events?$query")
        return AuditPage(
            entries = response.content.map(AuditEventDto::toDomain),
            totalPages = response.totalPages,
        )
    }

    override suspend fun getEntry(id: String): AuditEntry = get<AuditEventDto>("/audit-events/$id").toDomain()

    suspend fun loadEntries(
        page: Int,
        size: Int,
        from: String?,
        to: String?,
    ): AuditPage = loadEntries(page, size, from, to, null)

    private suspend inline fun <reified T> get(path: String): T {
        val response =
            httpClient.get(baseUrl + path) {
                authRepository.session.value?.token?.let { token ->
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
            }
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<AuditApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response.body()
    }
}

@Serializable
private data class AuditPageDto<T>(
    val content: List<T> = emptyList(),
    val totalPages: Int = 1,
)

@Serializable
private data class AuditEventDto(
    val id: String,
    val occurredAt: String,
    val actorUserId: String? = null,
    val actorUsername: String? = null,
    val actorProfile: String? = null,
    val action: String,
    val resourceType: String? = null,
    val resourceId: String? = null,
    val httpStatus: Int? = null,
    val details: Map<String, JsonElement> = emptyMap(),
) {
    fun toDomain(): AuditEntry {
        val detailsText =
            details.entries.joinToString(
                separator = ", ",
            ) { (key, value) -> "$key=${value.displayValue()}" }
        return AuditEntry(
            id = id,
            occurredAt = occurredAt,
            code = action,
            userName = actorUsername ?: actorUserId.orEmpty(),
            module = resourceType ?: "Sistema",
            description = detailsText.ifEmpty { resourceId ?: action },
            result = httpStatus.toAuditResult(),
            actorProfile = actorProfile,
            resourceId = resourceId,
            httpStatus = httpStatus,
            details = detailsText,
        )
    }
}

@Serializable
private data class AuditApiErrorDto(
    val message: String? = null,
)

private fun Int?.toAuditResult(): AuditResult =
    when {
        this == null -> AuditResult.NONE
        this in 200..399 -> AuditResult.SUCCESS
        else -> AuditResult.FAILURE
    }

private fun JsonElement.displayValue(): String = if (this is JsonPrimitive && isString) content else toString()
